// src/api.js

// Available API environments - add new enterprise environments as needed
const API_ENVIRONMENTS = [
  'https://pilot.citemed.com',     // Pilot
  'https://demo.citemed.com',      // Demo
  'https://review.citemed.io',     // Review
  // Add enterprise-specific URLs here as needed
  // 'https://acme.citemed.com',
];

const DISCOVERY_TIMEOUT = 5000; // 5 seconds per environment
const CACHE_KEY = 'apiBaseUrl';

class ApiClient {
  constructor() {
    this.baseURL = null;
    this.apiKey = null;
  }

  setApiKey(apiKey) {
    this.apiKey = apiKey;
  }

  setBaseURL(baseURL) {
    this.baseURL = baseURL;
  }

  async request(endpoint, options = {}) {
    if (!this.baseURL) {
      throw new Error('Base URL not set. Please authenticate first.');
    }

    const fullURL = `${this.baseURL}${endpoint}`;

    const headers = {
      ...options.headers,
      'x-api-key': this.apiKey,
      'Content-Type': 'application/json',
    };

    const response = await fetch(fullURL, {
      ...options,
      headers,
    });

    // Handle token expiration globally
    if (response.status === 401) {
      // Could emit event or use a callback to trigger token refresh
      throw new Error('Unauthorized');
    }

    return response;
  }

  get(endpoint, options = {}) {
    return this.request(endpoint, { method: 'GET', ...options });
  }

  post(endpoint, body, options = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
      ...options,
    });
  }

  async login(username, password) {
    const response = await fetch(`${this.baseURL}/api/token/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });
    const data = await response.json();
    if (response.ok) {
      return data;
    }
    return null;
  }

  async me() {
    const url = `/citesource/api/me/`;
    const res = await this.get(url);
    if (!res.ok) throw new Error("Fetching user data failed");
    return res.json();
  };

  /**
   * Validates API key against a specific environment with timeout
   * @param {string} baseURL - Environment URL to check
   * @param {string} apiKey - API key to validate
   * @returns {Promise<{success: boolean, baseURL: string, canonicalURL?: string, error?: string}>}
   */
  async validateAgainstEnvironment(baseURL, apiKey) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), DISCOVERY_TIMEOUT);

    try {
      const response = await fetch(`${baseURL}/citesource/api/me/`, {
        method: 'GET',
        headers: {
          'x-api-key': apiKey,
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        const data = await response.json();
        // Check for canonical_base_url in response for future-proofing
        const canonicalURL = data.canonical_base_url || baseURL;
        return { success: true, baseURL, canonicalURL };
      }

      return { success: false, baseURL, error: `Status ${response.status}` };
    } catch (error) {
      clearTimeout(timeoutId);

      // Check if it's a CORS error
      if (error.name === 'TypeError' && error.message === 'Load failed') {
        return { success: false, baseURL, error: 'CORS blocked' };
      }

      if (error.name === 'AbortError') {
        return { success: false, baseURL, error: 'Timeout' };
      }

      return { success: false, baseURL, error: error.message };
    }
  }

  /**
   * Discovers which environment the API key belongs to by trying all environments in parallel
   * @param {string} apiKey - API key to validate
   * @returns {Promise<string>} - The base URL of the correct environment
   * @throws {Error} - If key is invalid, collision detected, or network issues
   */
  async discoverEnvironment(apiKey) {
    // Try all environments in parallel
    const validationPromises = API_ENVIRONMENTS.map(env =>
      this.validateAgainstEnvironment(env, apiKey)
        .then(result => ({ status: 'fulfilled', value: result }))
        .catch(error => ({ status: 'rejected', reason: error }))
    );

    const results = await Promise.all(validationPromises);

    // Collect successful validations
    const successfulEnvs = results
      .filter(result => result.status === 'fulfilled' && result.value.success)
      .map(result => result.value);

    // Handle different result scenarios
    if (successfulEnvs.length === 0) {
      // Check if all requests timed out or failed
      const allTimedOut = results.every(result =>
        result.status === 'fulfilled' && result.value.error === 'Timeout'
      );

      if (allTimedOut) {
        throw new Error('Unable to reach CiteMed servers. Please check your internet connection.');
      }

      throw new Error('Invalid API key. Please check your credentials.');
    }

    if (successfulEnvs.length === 1) {
      // Success! Return the canonical URL (allowing for server-side redirects)
      const discoveredURL = successfulEnvs[0].canonicalURL || successfulEnvs[0].baseURL;
      this.cacheBaseURL(discoveredURL);
      return discoveredURL;
    }

    // Multiple successes - this should never happen with proper UUIDs
    if (successfulEnvs.length > 1) {
      const envList = successfulEnvs.map(e => e.baseURL).join(', ');
      throw new Error(
        `API key collision detected across multiple environments (${envList}). Please contact support@citemed.com`
      );
    }
  }

  /**
   * Caches the discovered base URL in Office roaming settings
   * @param {string} baseURL - The base URL to cache
   */
  cacheBaseURL(baseURL) {
    if (typeof Office !== 'undefined' && Office.context?.roamingSettings) {
      Office.context.roamingSettings.set(CACHE_KEY, baseURL);
      Office.context.roamingSettings.saveAsync(() => {});
    }
  }

  /**
   * Retrieves cached base URL from Office roaming settings
   * @returns {string|null} - Cached base URL or null if not found
   */
  getCachedBaseURL() {
    if (typeof Office !== 'undefined' && Office.context?.roamingSettings) {
      return Office.context.roamingSettings.get(CACHE_KEY) || null;
    }
    return null;
  }

  /**
   * Clears cached base URL from Office roaming settings
   */
  clearCachedBaseURL() {
    if (typeof Office !== 'undefined' && Office.context?.roamingSettings) {
      Office.context.roamingSettings.remove(CACHE_KEY);
      Office.context.roamingSettings.saveAsync(() => {});
    }
  }

  /**
   * Authenticates with API key discovery
   * Checks cache first, then discovers environment if needed
   * @param {string} apiKey - API key to authenticate with
   * @returns {Promise<string>} - The discovered/cached base URL
   */
  async authenticateWithDiscovery(apiKey) {
    // Check cache first
    const cachedURL = this.getCachedBaseURL();

    if (cachedURL) {
      // Validate cached URL still works
      const validation = await this.validateAgainstEnvironment(cachedURL, apiKey);

      if (validation.success) {
        const finalURL = validation.canonicalURL || cachedURL;
        if (finalURL !== cachedURL) {
          // Server provided a new canonical URL, update cache
          this.cacheBaseURL(finalURL);
        }
        return finalURL;
      }

      // Cache is stale, clear it and rediscover
      this.clearCachedBaseURL();
    }

    // Discover environment
    const discoveredURL = await this.discoverEnvironment(apiKey);
    return discoveredURL;
  }

  async getArticles(page_number, query, project_id, options = {}) {
    const { states, tagIds, deviceIds, year, listId, signal } = options;
    const params = new URLSearchParams();
    params.append('page_number', page_number);
    if (query) params.append('search_term', query);
    if (project_id) params.append('project', project_id);
    if (states && states.length) params.append('state_filter', states.join(','));
    if (tagIds && tagIds.length) params.append('tag_filter', tagIds.join(','));
    if (deviceIds && deviceIds.length) params.append('device_filter', deviceIds.join(','));
    if (year) params.append('year_filter', year);
    if (listId) params.append('list_id', listId);

    const url = `/citesource/api/references/?${params.toString()}`;
    const res = await this.get(url, { signal });
    if (!res.ok) throw new Error("Citation search failed");
    return res.json();
  };

  async getProjectTags(project_id, { signal } = {}) {
    const res = await this.get(`/citesource/api/projects/${project_id}/tags/`, { signal });
    if (!res.ok) throw new Error("Failed to load project tags");
    return res.json();
  };

  // Filter options for the reference search: { devices, projects, tags, lit_reviews, ... }.
  async getFilterOptions({ signal } = {}) {
    const res = await this.get(`/citesource/api/library-entry-filters/`, { signal });
    if (!res.ok) throw new Error("Failed to load filter options");
    return res.json();
  };
  async getCitations(style, articles, responseType = "bibliography", citationFormat = "text") {
    let url = `/literature_reviews/api/citations/`;
    const params = new URLSearchParams();
    
    if (style) {
      params.append('style', style);
    }
    if (articles) {
      params.append('articles', articles);
    }
    if (responseType) {
      params.append('response_type', responseType);
    }
    if (citationFormat) {
      params.append('citation_format', citationFormat);
    }
    
    url += `?${params.toString()}`;
    const res = await this.get(url);
    if (!res.ok) throw new Error("Get citations failed");
    return res.json();
  };
}

export const apiClient = new ApiClient();
